/* eslint-disable @typescript-eslint/ban-ts-comment */
import {
	ButtonGroup,
	Flex,
	IconButton,
	Pagination,
	Skeleton,
	Stack,
	Table,
} from '@welbe/design-system';
import { LuChevronLeft, LuChevronRight } from 'react-icons/lu';

type Column = {
	id: string;
	label: string;
	width?: string;
	hidden?: boolean;
};

type Row = Record<string, React.ReactNode>;

type UsersTableProps = {
	headers: Column[];
	rows: Row[];
	loading: boolean;
	currentPage: number;
	totalAmount: number;
	pageSize: number;
	onPageChange: (page: number) => void;
};

export const UsersTable = ({
	headers,
	rows,
	loading,
	currentPage,
	totalAmount,
	pageSize,
	onPageChange,
}: UsersTableProps) => {
	const visibleColumns = headers.filter((column) => !column.hidden);

	return (
		<Stack maxH="calc(100vh - 250px)" w="100%" overflowX="auto" m="0">
			<Table.ScrollArea borderWidth="1px" w="100%" overflowX="auto">
				<Table.Root size="sm" stickyHeader>
					<Table.Header>
						<Table.Row>
							{headers.map((column) => (
								<Table.ColumnHeader
									hidden={column.hidden}
									key={column.id}
									minW={column.width}
									fontWeight="600"
									color="blue.500"
								>
									{column.label}
								</Table.ColumnHeader>
							))}
						</Table.Row>
					</Table.Header>
					<Table.Body h="100%">
						{loading
							? Array.from({ length: 6 }).map((_, index) => (
									<Table.Row key={index}>
										{visibleColumns.map((column) => (
											<Table.Cell key={column.id}>
												<Skeleton.Root>
													<Skeleton.Text width="100%" height="20px" />
												</Skeleton.Root>
											</Table.Cell>
										))}
									</Table.Row>
							  ))
							: rows.map((row, index) => (
									<Table.Row
										key={`${row.id}-${index}`}
										fontWeight="500"
										color="gray.700"
										fontSize="xs"
										_hover={{ bg: 'gray.100' }}
									>
										{visibleColumns.map((column) => (
											<Table.Cell key={column.id}>{row[column.id]}</Table.Cell>
										))}
									</Table.Row>
							  ))}
					</Table.Body>
				</Table.Root>
			</Table.ScrollArea>
			<Flex justifyContent="center">
				<Pagination.Root
					count={totalAmount}
					page={currentPage + 1}
					pageSize={pageSize}
					onPageChange={(details: { page: number }) => {
						onPageChange(details.page - 1);
					}}
				>
					<ButtonGroup variant="ghost" size="xs" wrap="wrap">
						{/* @ts-ignore */}
						<Pagination.PrevTrigger asChild>
							<IconButton>
								<LuChevronLeft />
							</IconButton>
						</Pagination.PrevTrigger>

						<Pagination.Items
							render={(page) => (
								<IconButton variant={{ base: 'ghost', _selected: 'solid' }}>
									{page.value}
								</IconButton>
							)}
						/>

						{/* @ts-ignore */}
						<Pagination.NextTrigger asChild>
							<IconButton>
								<LuChevronRight />
							</IconButton>
						</Pagination.NextTrigger>
					</ButtonGroup>
				</Pagination.Root>
			</Flex>
		</Stack>
	);
};
